import { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import toast from "react-hot-toast";
import { BASE_URL, API_ENDPOINTS } from "../../config/api.config";

const DuplicateEvent = ({ event, onClose, onSuccess }) => {
  const initialDate = useMemo(() => event?.date || event?.eventDate || "", [event]);
  const initialTime = useMemo(() => event?.time || "", [event]);
  const [date, setDate] = useState(initialDate);
  const [time, setTime] = useState(initialTime);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setDate(initialDate);
    setTime(initialTime);
  }, [initialDate, initialTime]);

  const handleDuplicate = async () => {
    if (!event?.id) {
      toast.error("Unable to determine event to duplicate.");
      return;
    }

    if (!date) {
      toast.error("Please select a date for the new event.");
      return;
    }

    const token = localStorage.getItem("accessToken");
    if (!token) {
      toast.error("Authentication required. Please sign in again.");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        eventId: event.id,
        date,
      };

      if (time) {
        payload.time = time;
      }

      const response = await fetch(`${BASE_URL}${API_ENDPOINTS.NONPROFIT_COPY_EVENT}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json().catch(() => null);
      const statusCode = result?.statusCode ?? response.status;

      if (!response.ok || statusCode >= 400) {
        throw new Error(result?.message || "Failed to duplicate event.");
      }

      toast.success("Event duplicated successfully.");
      onSuccess?.(result?.data);
    } catch (error) {
      toast.error(error?.message || "Failed to duplicate event.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!event) {
    return null;
  }

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-50 transition-opacity"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center ">
        <div className="rounded-lg overflow-hidden shadow-2xl w-[90%] lg:w-[30%] bg-white">
          <div className="flex items-center justify-center p-4 mb-4 text-black relative border-b border-border">
            <div>
              <h2 className="text-lg lg:text-xl font-popinsSemiBold">Duplicate Event</h2>
              <p className="text-xs text-center text-black/60 mt-1 px-4">{event.title}</p>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 absolute right-3 top-3 bg-white/20 hover:bg-white/30 rounded-full text-lg flex items-center justify-center transition-all hover:rotate-90"
            >
              <X className="w-6 h-6 text-foreground" />
            </button>
          </div>
          <div>
            <div className="w-[90%] mx-auto mt-5">
              <div className="w-full">
                <label className="font-popinsMedium text-sm">Date</label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full mt-2 font-popinsMedium rounded-md border-2 border-border bg-white px-4 py-2 text-black/80 outline-none transition-all duration-300 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3] cursor-pointer"
                />
              </div>
              <div className="w-full mt-4">
                <label className="font-popinsMedium text-sm">Time</label>
                <input
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  className="w-full mt-2 font-popinsMedium rounded-md border-2 border-border bg-white px-4 py-2 text-black/80 outline-none transition-all duration-300 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3] cursor-pointer"
                />
              </div>
            </div>
            <div className="flex justify-center w-[90%] mx-auto gap-x-3 mb-6">
              <button
                type="button"
                className="relative overflow-hidden py-3 px-8 bg-[#F0F2F5] text-black font-popinsSemiBold text-sm mt-8 rounded-md shadow-lg transform transition duration-300 ease-in-out hover:bg-[#F0F2F5]/90 hover:scale-105 active:scale-95 active:shadow-inner focus:outline-none disabled:opacity-50"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
                <span className="absolute inset-0 rounded-full bg-white opacity-0 active:opacity-10 transition-opacity duration-300"></span>
              </button>
              <button
                type="button"
                className="relative overflow-hidden py-3 px-12 bg-themeColor text-white font-popinsSemiBold text-sm mt-8 rounded-md shadow-lg transform transition duration-300 ease-in-out hover:bg-themeColor/90 hover:scale-105 active:scale-95 active:shadow-inner focus:outline-none disabled:opacity-50"
                onClick={handleDuplicate}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Duplicating..." : "Duplicate"}
                <span className="absolute inset-0 rounded-full bg-white opacity-0 active:opacity-10 transition-opacity duration-300"></span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DuplicateEvent;
